/**
 * Sentry Error Tracking Configuration
 * 
 * This module initializes and configures Sentry for production error tracking.
 * Optimized for Sentry free tier with smart filtering and sampling.
 * 
 * Features:
 * - Environment-based initialization (production only)
 * - Smart error filtering to avoid quota exhaustion
 * - User context tracking (sanitized)
 * - Breadcrumb tracking for debugging
 * - Performance monitoring (optional)
 * - Release tracking
 */

import * as Sentry from '@sentry/react';

/**
 * Error types that should NOT be reported to Sentry
 * These are expected errors that don't indicate bugs
 */
const IGNORED_ERROR_PATTERNS = [
  // Expected HTTP errors
  /401/i, // Unauthorized - handled by auth flow
  /403/i, // Forbidden - handled by RBAC
  /404/i, // Not Found - expected for missing resources
  
  // Expected validation errors
  /validation/i,
  /invalid input/i,
  
  // Network errors that are user-side issues
  /network error/i,
  /failed to fetch/i,
  
  // Browser extension errors
  /chrome-extension/i,
  /moz-extension/i,
  
  // ResizeObserver errors (benign)
  /ResizeObserver loop/i,
];

/**
 * Check if an error should be ignored
 */
function shouldIgnoreError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  return IGNORED_ERROR_PATTERNS.some(pattern => 
    pattern.test(errorMessage)
  );
}

/**
 * Check if Sentry should be enabled based on environment
 */
function shouldEnableSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const isProduction = import.meta.env.MODE === 'production';
  const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';

  // Always enable in production if DSN is configured
  if (isProduction && dsn) {
    return true;
  }

  // In development, only enable if explicitly configured
  if (!isProduction && dsn && enableInDev) {
    return true;
  }

  return false;
}

/**
 * Initialize Sentry with production-ready configuration
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking disabled.');
    return;
  }

  if (!shouldEnableSentry()) {
    console.log('[Sentry] Disabled in development. Set VITE_SENTRY_ENABLE_IN_DEV=true to enable.');
    return;
  }

  // Log initialization mode
  const isDevelopment = import.meta.env.MODE !== 'production';
  if (isDevelopment) {
    console.log('[Sentry] Initializing in development mode for testing/demo');
  }

  Sentry.init({
    dsn,

    // Environment and release tracking
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Integrations
    integrations: [
      // React-specific error tracking with browser tracing
      Sentry.browserTracingIntegration({
        // Track navigation performance
        tracePropagationTargets: [
          'localhost',
          /^\//,
          import.meta.env.VITE_AI_API_URL,
        ],
      }),

      // Replay sessions for debugging (optional, uses quota)
      // Sentry.replayIntegration({
      //   maskAllText: true,
      //   blockAllMedia: true,
      // }),
    ],
    
    // Performance Monitoring
    // Set to 0.1 (10%) to stay within free tier limits
    tracesSampleRate: 0.1,
    
    // Session Replay (optional, disabled by default to save quota)
    // replaysSessionSampleRate: 0.1,
    // replaysOnErrorSampleRate: 1.0,
    
    // Error filtering - CRITICAL for free tier
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Ignore expected errors
      if (error && shouldIgnoreError(error as Error)) {
        return null;
      }
      
      // Sample network timeouts (only report 10%)
      if (error && /timeout/i.test((error as Error).message)) {
        return Math.random() < 0.1 ? event : null;
      }
      
      // Sanitize sensitive data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }
      
      return event;
    },
    
    // Breadcrumb filtering
    beforeBreadcrumb(breadcrumb) {
      // Don't track console logs in production
      if (breadcrumb.category === 'console') {
        return null;
      }
      
      // Sanitize URLs with tokens
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = breadcrumb.data.url.replace(/token=[^&]+/, 'token=REDACTED');
      }
      
      return breadcrumb;
    },
    
    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      
      // Random plugins/extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      
      // Network errors
      'NetworkError',
      'Network request failed',
      
      // ResizeObserver
      'ResizeObserver loop limit exceeded',
    ],
  });

  console.log('[Sentry] Initialized successfully');
}

/**
 * Manually capture an exception with context
 */
export function captureException(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: Sentry.SeverityLevel;
  }
): void {
  // Always log to console for debugging
  console.error('[Sentry]', error, context);

  // Check if Sentry should be enabled
  if (!shouldEnableSentry()) {
    console.log('[Sentry] Error not sent - Sentry is disabled');
    return;
  }

  // Send to Sentry
  Sentry.captureException(error, {
    level: context?.level || 'error',
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Set user context for error tracking
 * IMPORTANT: Only set non-sensitive user data
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  tenantId?: string;
  roles?: string[];
} | null): void {
  // Check if Sentry should be enabled
  if (!shouldEnableSentry()) {
    return;
  }

  if (user) {
    Sentry.setUser({
      id: user.id,
      // Hash or anonymize email if needed
      email: user.email,
      tenant_id: user.tenantId,
      roles: user.roles?.join(','),
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>
): void {
  // Check if Sentry should be enabled
  if (!shouldEnableSentry()) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

